import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getHelpArticleUrl,
  getHelpArticlesForPersona,
  getHelpCenterBaseUrl,
  groupHelpArticlesByModule,
  HelpPersona,
} from "../../helpers/helpCenter";
import { PLAY_STORE_URL } from "../../helpers/playStore";
import playStoreQr from "../../assets/images/store/play-store-qr.png";
import playStoreBadge from "../../assets/images/store/google-play-badge.png";

const PERSONAS: HelpPersona[] = ["receptionist", "professional", "manager", "owner"];

const HelpPage = () => {
  const { t, i18n } = useTranslation();
  const [activePersona, setActivePersona] = useState<HelpPersona>("receptionist");

  const helpCenterUrl = useMemo(() => getHelpCenterBaseUrl(), []);

  const groupedArticles = useMemo(() => {
    const articles = getHelpArticlesForPersona(activePersona);

    return groupHelpArticlesByModule(articles);
  }, [activePersona]);

  useEffect(() => {
    document.title = t("help.pageTitle");
  }, [t]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("help.breadcrumb.title")}
          pageTitle={t("help.breadcrumb.pageTitle")}
        />
        <Row className="justify-content-center">
          <Col xl={10}>
            <Card className="mb-4">
              <CardHeader>
                <h5 className="card-title mb-0">{t("help.title")}</h5>
              </CardHeader>
              <CardBody>
                <p className="text-muted">{t("help.intro")}</p>
                <div className="d-flex flex-wrap gap-2">
                  <a
                    href={helpCenterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    {t("help.openCenter")}
                  </a>
                  <a
                    href={getHelpArticleUrl("primeiros-passos/criar-conta", i18n.language)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-soft-secondary"
                  >
                    {t("help.openGettingStarted")}
                  </a>
                </div>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <h5 className="card-title mb-0">{t("playStore.helpTitle")}</h5>
              </CardHeader>
              <CardBody>
                <div className="d-flex flex-wrap align-items-center gap-4">
                  <img
                    src={playStoreQr}
                    alt={t("playStore.qrAlt")}
                    width={120}
                    height={120}
                    className="rounded border bg-white p-2 flex-shrink-0"
                  />
                  <div className="flex-grow-1">
                    <p className="text-muted mb-3">{t("playStore.helpBody")}</p>
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-inline-block"
                    >
                      <img
                        src={playStoreBadge}
                        alt={t("playStore.badgeAlt")}
                        height={56}
                        style={{ width: "auto" }}
                      />
                    </a>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-2">{t("help.guidesByPersona")}</h5>
                <p className="text-muted small mb-0">{t("help.guidesByPersonaHint")}</p>
              </CardHeader>
              <CardBody>
                <Nav pills className="nav-pills-custom mb-4 flex-wrap gap-2">
                  {PERSONAS.map((persona) => (
                    <NavItem key={persona}>
                      <NavLink
                        href="#"
                        className={activePersona === persona ? "active" : ""}
                        onClick={(event) => {
                          event.preventDefault();
                          setActivePersona(persona);
                        }}
                      >
                        {t(`help.personas.${persona}`)}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                {groupedArticles.map((group) => (
                  <div key={group.moduleKey} className="mb-4">
                    <h6 className="text-muted text-uppercase fs-12 mb-3">
                      {t(group.moduleKey)}
                    </h6>
                    <Row className="g-3">
                      {group.articles.map((article) => (
                        <Col key={article.path} md={6} lg={4}>
                          <a
                            href={getHelpArticleUrl(article.path, i18n.language)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-block border rounded p-3 h-100 text-reset text-decoration-none help-guide-card"
                          >
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              <span className="fw-medium">{t(article.labelKey)}</span>
                              <i className="ri-external-link-line text-muted" />
                            </div>
                          </a>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HelpPage;
